import useSWR from "swr"

import { OPENAPI_ROLE_MAPPING } from "./roleMapping"
import { useGetUserData } from "../../../../hooks/useGetUserData"
import { getClientProtected } from "../../clients/clients"
import { HYBRID_API_DOCS } from "../../urls/hybrid"

type TOpenApiJson = { [key: string]: any } | undefined
interface IUseOpenApi {
    openApiJson: TOpenApiJson | undefined
    isError: boolean
}

/**
 * Hook for retrieving the hybrid API open API documentation json. This
 * json is injected into redoc react reader component
 * @returns {IUseOpenApi} - open API json, loading and error states
 */
const useOpenApi = (): IUseOpenApi => {
    const { groups } = useGetUserData()

    /**
     * Filter the open API json to only include the paths that the user has
     * permission to access
     * @param {TOpenApiJson} data - open API json
     * @returns {TOpenApiJson} - filtered open API json
     */
    const openapiPermissions = (data: TOpenApiJson): TOpenApiJson => {
        if (groups && groups.length > 0 && data !== undefined) {
            // Only allow the values that are in the role mapping for all of the user's groups
            const allowedPaths = groups
                .map(
                    (group) =>
                        group in OPENAPI_ROLE_MAPPING &&
                        OPENAPI_ROLE_MAPPING[group]
                )
                .flat()

            // ENA Sys Admin
            if (allowedPaths.includes("*")) {
                return data
            }

            // Filter the data object to only include the allowed keys in typescript
            const newData = data
            const filteredPaths = Object.keys(newData.paths)
                .filter((key) => allowedPaths.includes(key))
                .reduce(
                    (obj: any, key) => ({
                        ...obj,
                        [key]: newData.paths[key],
                    }),
                    {}
                )
            newData.paths = filteredPaths
            return newData
        }

        return data
    }

    /**
     * Remove the /api from the open API json as it is not needed
     * @param {TOpenApiJson} data - open API json
     * @returns {TOpenApiJson} - open API json with server removed
     */
    const removeServer = (data: TOpenApiJson): TOpenApiJson => {
        if (
            data !== undefined &&
            data.servers.length > 0 &&
            data.servers[0].url === "/api"
        ) {
            // Remove the server from the open API json
            const newData = data
            newData.servers[0].url = `/`
            return newData
        }

        return data
    }

    const { data: openApiJson, error } = useSWR(
        HYBRID_API_DOCS,
        async (url: string): Promise<TOpenApiJson> => {
            try {
                const res = await getClientProtected(url)
                // Trust the open API format as passing straight to redoc
                const updatedRes = removeServer(res as TOpenApiJson)
                const filteredPaths = openapiPermissions(updatedRes)
                return filteredPaths
            } catch {
                throw Error()
            }
        }
    )

    return {
        openApiJson,
        isError: error,
    }
}

export default useOpenApi
